import { useState, useRef } from 'react'
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Alert,
    Divider,
    IconButton,
    Tooltip,
    Avatar,
    CircularProgress,
} from '@mui/material'
import {
    ContentCopy,
    Chat as ChatIcon,
    Code,
    ColorLens,
    CloudUpload,
    Palette,
    Title,
    Image as ImageIcon,
    Circle,
    Visibility,
    Close,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import api from '@api/axios'

const API_URL = import.meta.env.VITE_API_URL || '/api'

/**
 * Live Chat Settings Component
 * Full appearance & branding editor with live preview
 */
const LiveChatSettings = ({ formId, formData, setFormData, language }) => {
    const [embedCopied, setEmbedCopied] = useState(false)
    const [showPreview, setShowPreview] = useState(false)
    const [uploadingLogo, setUploadingLogo] = useState(false)
    const logoInputRef = useRef(null)

    const isRTL = language === 'ar'
    const settings = formData.settings || {}
    const isEnabled = !!formData.enable_live_chat

    const updateSettings = (key, value) => {
        setFormData({
            ...formData,
            settings: { ...settings, [key]: value }
        })
    }

    const handleToggle = (e) => {
        setFormData({ ...formData, enable_live_chat: e.target.checked ? 1 : 0 })
    }

    // -- Colors --
    const primaryColor = settings.chat_primary_color || '#1a3a6b'
    const secondaryColor = settings.chat_secondary_color || '#0d2247'
    const headerTitle = settings.chat_header_title_en || settings.chat_header_title || formData.title || 'Live Chat'
    const headerTitleAr = settings.chat_header_title_ar || formData.title_ar || 'محادثة مباشرة'
    const headerSubtitle = settings.chat_header_subtitle_en || settings.chat_header_subtitle || 'Start a conversation'
    const headerSubtitleAr = settings.chat_header_subtitle_ar || 'ابدأ محادثة'
    const logoUrl = settings.chat_logo_url || ''

    // -- Logo upload --
    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        e.target.value = ''

        if (!file.type.startsWith('image/')) {
            toast.error(isRTL ? 'يرجى اختيار صورة' : 'Please select an image file')
            return
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error(isRTL ? 'الحجم الأقصى 2MB' : 'Max file size is 2MB')
            return
        }

        setUploadingLogo(true)
        try {
            const fd = new FormData()
            fd.append('logo', file)
            fd.append('form_id', formId)

            const res = await api.post('/chat/upload-logo', fd, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
            const url = res.data?.url || res.url
            if (url) {
                updateSettings('chat_logo_url', url)
                toast.success(isRTL ? 'تم رفع الشعار' : 'Logo uploaded successfully')
            }
        } catch (err) {
            console.error('Logo upload error:', err)
            toast.error(isRTL ? 'فشل رفع الشعار' : 'Logo upload failed')
        } finally {
            setUploadingLogo(false)
        }
    }

    const baseUrl = window.location.origin
    const publicChatUrl = `${baseUrl}/chat/${formId}`
    const embedCode = `<!-- Live Chat Widget -->\n<script>\n  (function() {\n    var d = document, s = d.createElement('script');\n    s.src = '${baseUrl}/chat-widget.js';\n    s.setAttribute('data-form-id', '${formId}');\n    s.setAttribute('data-color', '${primaryColor}');\n    s.async = true;\n    d.head.appendChild(s);\n  })();\n</script>`

    const handleCopyEmbed = () => {
        navigator.clipboard.writeText(embedCode)
        setEmbedCopied(true)
        toast.success(isRTL ? 'تم نسخ الكود' : 'Embed code copied!')
        setTimeout(() => setEmbedCopied(false), 2000)
    }

    const handleCopyUrl = () => {
        navigator.clipboard.writeText(publicChatUrl)
        toast.success(isRTL ? 'تم نسخ الرابط' : 'Chat URL copied!')
    }

    /* ── Color Picker Row ── */
    const ColorRow = ({ label, labelAr, value, settingKey }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
                component="input"
                type="color"
                value={value}
                onChange={(e) => updateSettings(settingKey, e.target.value)}
                sx={{
                    width: 44, height: 44, border: '2px solid', borderColor: 'divider',
                    borderRadius: 2, cursor: 'pointer', p: 0.3,
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: 'primary.main', transform: 'scale(1.05)' },
                }}
            />
            <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                    {isRTL ? labelAr : label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {value}
                </Typography>
            </Box>
        </Box>
    )

    /* ── Live Preview ── */
    const LivePreview = () => {
        const gradient = `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
        const fileBase = API_URL.replace(/\/api\/?$/, '/api')
        const fullLogoUrl = logoUrl ? (logoUrl.startsWith('http') ? logoUrl : `${fileBase}${logoUrl}`) : null

        return (
            <Paper elevation={0} sx={{
                width: '100%', maxWidth: 340, mx: 'auto',
                borderRadius: 3, overflow: 'hidden',
                border: '1px solid', borderColor: 'divider',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            }}>
                {/* Header */}
                <Box sx={{
                    background: gradient, color: '#fff', p: 2,
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    position: 'relative', overflow: 'hidden',
                    '&::after': {
                        content: '""', position: 'absolute',
                        top: -25, right: -25, width: 80, height: 80,
                        borderRadius: '50%', background: 'rgba(255,255,255,0.07)',
                    },
                }}>
                    <Box sx={{
                        width: 42, height: 42, borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.95)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}>
                        {fullLogoUrl ? (
                            <Box component="img" src={fullLogoUrl} alt="Logo"
                                sx={{ width: 28, height: 28, objectFit: 'contain' }}
                            />
                        ) : (
                            <ChatIcon sx={{ fontSize: 24, color: primaryColor }} />
                        )}
                    </Box>
                    <Box sx={{ zIndex: 1 }}>
                        <Typography variant="body1" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: '0.95rem' }}>
                            {headerTitle}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                            <Box sx={{
                                width: 6, height: 6, borderRadius: '50%', bgcolor: '#4caf50',
                                boxShadow: '0 0 6px #4caf50',
                            }} />
                            <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 500, fontSize: '0.7rem' }}>
                                {headerSubtitle}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Messages preview */}
                <Box sx={{ p: 2, bgcolor: '#f8f9fb', minHeight: 120 }}>
                    {/* Visitor message */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <Box sx={{
                            px: 1.5, py: 0.8, borderRadius: '14px 14px 3px 14px',
                            bgcolor: primaryColor, color: '#fff', fontSize: 13,
                            maxWidth: '70%', boxShadow: `0 2px 8px ${primaryColor}30`,
                        }}>
                            Hello 👋
                        </Box>
                    </Box>
                    {/* Agent message */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                        <Box sx={{
                            px: 1.5, py: 0.8, borderRadius: '14px 14px 14px 3px',
                            bgcolor: '#fff', color: 'text.primary', fontSize: 13,
                            maxWidth: '70%', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                        }}>
                            {settings.chat_welcome_message || 'How can we help you?'}
                        </Box>
                    </Box>
                </Box>

                {/* Input mock */}
                <Box sx={{
                    p: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fff',
                    display: 'flex', alignItems: 'center', gap: 1,
                }}>
                    <Box sx={{
                        flex: 1, py: 0.8, px: 1.5, bgcolor: '#f5f6f8',
                        borderRadius: 2, fontSize: 12, color: 'grey.400',
                    }}>
                        Type a message...
                    </Box>
                    <Box sx={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: gradient, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Typography sx={{ color: '#fff', fontSize: 14 }}>➤</Typography>
                    </Box>
                </Box>
            </Paper>
        )
    }

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ChatIcon color="primary" />
                <Typography variant="h6">
                    {isRTL ? 'المحادثة المباشرة' : 'Live Chat'}
                </Typography>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
                {isRTL
                    ? 'فعّل المحادثة المباشرة للسماح للزوار بالتواصل مع فريقك بشكل فوري. خصص المظهر ليتناسب مع هوية علامتك التجارية.'
                    : 'Enable live chat to let visitors chat with your team in real-time. Customize the appearance to match your brand.'}
            </Alert>

            {/* Enable Toggle */}
            <FormControlLabel
                control={<Switch checked={isEnabled} onChange={handleToggle} color="primary" />}
                label={
                    <Typography fontWeight={600}>
                        {isRTL ? 'تفعيل المحادثة المباشرة' : 'Enable Live Chat'}
                    </Typography>
                }
                sx={{ mb: 3 }}
            />

            {isEnabled && (
                <Box>
                    <Divider sx={{ mb: 3 }} />

                    {/* ════════════ APPEARANCE SECTION ════════════ */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Palette fontSize="small" color="action" />
                        <Typography variant="subtitle1" fontWeight={700}>
                            {isRTL ? 'المظهر والعلامة التجارية' : 'Appearance & Branding'}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {/* Left: Settings */}
                        <Box sx={{ flex: 1, minWidth: 280 }}>

                            {/* Logo */}
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                <ImageIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                {isRTL ? 'الشعار' : 'Logo'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <Box sx={{
                                    width: 64, height: 64, borderRadius: 2,
                                    border: '2px dashed', borderColor: logoUrl ? 'primary.main' : 'divider',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    bgcolor: logoUrl ? 'primary.50' : 'grey.50',
                                    overflow: 'hidden', cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
                                }}
                                    onClick={() => logoInputRef.current?.click()}
                                >
                                    {uploadingLogo ? (
                                        <CircularProgress size={24} />
                                    ) : logoUrl ? (
                                        <Box component="img"
                                            src={logoUrl.startsWith('http') ? logoUrl : `${API_URL.replace(/\/api\/?$/, '/api')}${logoUrl}`}
                                            alt="Logo"
                                            sx={{ width: '80%', height: '80%', objectFit: 'contain' }}
                                        />
                                    ) : (
                                        <CloudUpload sx={{ color: 'grey.400' }} />
                                    )}
                                </Box>
                                <Box>
                                    <Button
                                        size="small" variant="outlined"
                                        onClick={() => logoInputRef.current?.click()}
                                        disabled={uploadingLogo}
                                        startIcon={<CloudUpload />}
                                        sx={{ mb: 0.5, textTransform: 'none' }}
                                    >
                                        {isRTL ? 'رفع شعار' : 'Upload Logo'}
                                    </Button>
                                    {logoUrl && (
                                        <Typography
                                            variant="caption" color="error" sx={{ display: 'block', cursor: 'pointer' }}
                                            onClick={() => updateSettings('chat_logo_url', '')}
                                        >
                                            {isRTL ? 'إزالة' : 'Remove'}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {isRTL ? 'PNG أو SVG، حتى 2MB' : 'PNG or SVG, up to 2MB'}
                                    </Typography>
                                </Box>
                                <input type="file" ref={logoInputRef} hidden
                                    accept="image/png,image/svg+xml,image/jpeg,image/webp"
                                    onChange={handleLogoUpload}
                                />
                            </Box>

                            {/* Header Title (Bilingual) */}
                            <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
                                {isRTL ? 'عنوان الرأس' : 'Header Title'}
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                                <TextField
                                    fullWidth size="small"
                                    label="English (EN)"
                                    value={settings.chat_header_title_en || ''}
                                    onChange={(e) => updateSettings('chat_header_title_en', e.target.value)}
                                    placeholder={formData.title || 'Live Chat'}
                                />
                                <TextField
                                    fullWidth size="small"
                                    label="العربية (AR)"
                                    value={settings.chat_header_title_ar || ''}
                                    onChange={(e) => updateSettings('chat_header_title_ar', e.target.value)}
                                    placeholder={formData.title_ar || 'محادثة مباشرة'}
                                    inputProps={{ dir: 'rtl' }}
                                />
                            </Box>

                            {/* Header Subtitle (Bilingual) */}
                            <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
                                {isRTL ? 'النص الفرعي' : 'Header Subtitle'}
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                                <TextField
                                    fullWidth size="small"
                                    label="English (EN)"
                                    value={settings.chat_header_subtitle_en || ''}
                                    onChange={(e) => updateSettings('chat_header_subtitle_en', e.target.value)}
                                    placeholder="Start a conversation"
                                />
                                <TextField
                                    fullWidth size="small"
                                    label="العربية (AR)"
                                    value={settings.chat_header_subtitle_ar || ''}
                                    onChange={(e) => updateSettings('chat_header_subtitle_ar', e.target.value)}
                                    placeholder="ابدأ محادثة"
                                    inputProps={{ dir: 'rtl' }}
                                />
                            </Box>

                            {/* Colors */}
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
                                <ColorLens fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                {isRTL ? 'الألوان' : 'Colors'}
                            </Typography>

                            <ColorRow
                                label="Primary Color" labelAr="اللون الرئيسي"
                                value={primaryColor} settingKey="chat_primary_color"
                            />
                            <ColorRow
                                label="Secondary Color" labelAr="اللون الثانوي"
                                value={secondaryColor} settingKey="chat_secondary_color"
                            />

                            {/* Gradient Preview */}
                            <Box sx={{
                                height: 8, borderRadius: 4, mb: 3,
                                background: `linear-gradient(90deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                                boxShadow: `0 2px 8px ${primaryColor}30`,
                            }} />
                        </Box>

                        {/* Right: Live Preview */}
                        <Box sx={{ flex: 1, minWidth: 280 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <Visibility fontSize="small" color="action" />
                                <Typography variant="body2" fontWeight={600}>
                                    {isRTL ? 'معاينة مباشرة' : 'Live Preview'}
                                </Typography>
                            </Box>
                            <LivePreview />
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* ════════════ MESSAGES SECTION ════════════ */}
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                        {isRTL ? 'الرسائل' : 'Messages'}
                    </Typography>

                    <TextField
                        fullWidth
                        label={isRTL ? 'رسالة الترحيب (English)' : 'Welcome Message (English)'}
                        value={settings.chat_welcome_message || ''}
                        onChange={(e) => updateSettings('chat_welcome_message', e.target.value)}
                        placeholder="Hello! How can we help you today?"
                        sx={{ mb: 2 }}
                        multiline rows={8}
                    />

                    <TextField
                        fullWidth
                        label={isRTL ? 'رسالة الترحيب (عربي)' : 'Welcome Message (Arabic)'}
                        value={settings.chat_welcome_message_ar || ''}
                        onChange={(e) => updateSettings('chat_welcome_message_ar', e.target.value)}
                        placeholder="مرحباً! كيف يمكننا مساعدتك اليوم؟"
                        sx={{ mb: 2 }}
                        multiline rows={8} dir="rtl"
                    />

                    <TextField
                        fullWidth
                        label={isRTL ? 'رسالة عدم الاتصال (English)' : 'Offline Message (English)'}
                        value={settings.chat_offline_message || ''}
                        onChange={(e) => updateSettings('chat_offline_message', e.target.value)}
                        placeholder="We are currently offline. Please leave a message."
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        label={isRTL ? 'رسالة عدم الاتصال (عربي)' : 'Offline Message (Arabic)'}
                        value={settings.chat_offline_message_ar || ''}
                        onChange={(e) => updateSettings('chat_offline_message_ar', e.target.value)}
                        placeholder="نحن غير متصلين حالياً. يرجى ترك رسالة."
                        sx={{ mb: 2 }}
                        dir="rtl"
                    />

                    <Divider sx={{ my: 3 }} />

                    {/* ════════════ OPTIONS ════════════ */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!settings.chat_require_email}
                                onChange={(e) => updateSettings('chat_require_email', e.target.checked)}
                                color="primary" size="small"
                            />
                        }
                        label={isRTL ? 'طلب البريد الإلكتروني من الزائر' : 'Require visitor email'}
                        sx={{ mb: 3 }}
                    />

                    <Divider sx={{ my: 3 }} />

                    {/* ════════════ INTEGRATION & WIDGET ════════════ */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Code fontSize="small" color="action" />
                        <Typography variant="subtitle1" fontWeight={700}>
                            {isRTL ? 'التكامل والتضمين' : 'Integration & Widget'}
                        </Typography>
                    </Box>

                    {/* ── 1. Floating Chat Widget ── */}
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 0, mb: 3, borderRadius: 3, overflow: 'hidden',
                            border: '1px solid', borderColor: 'primary.200',
                        }}
                    >
                        {/* Section header */}
                        <Box sx={{
                            px: 2.5, py: 1.5,
                            background: `linear-gradient(135deg, ${primaryColor}12 0%, ${secondaryColor}08 100%)`,
                            borderBottom: '1px solid', borderColor: 'divider',
                            display: 'flex', alignItems: 'center', gap: 1.5,
                        }}>
                            <Box sx={{
                                width: 36, height: 36, borderRadius: '50%',
                                bgcolor: primaryColor, display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                boxShadow: `0 2px 8px ${primaryColor}40`,
                            }}>
                                <ChatIcon sx={{ fontSize: 18, color: '#fff' }} />
                            </Box>
                            <Box>
                                <Typography variant="body2" fontWeight={700}>
                                    {isRTL ? 'أيقونة المحادثة العائمة' : 'Floating Chat Widget'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {isRTL
                                        ? 'فقاعة عائمة تفتح نافذة محادثة فورية على أي موقع'
                                        : 'A floating bubble that opens a live chat modal on any website'}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ p: 2.5 }}>
                            {/* Visual preview of the widget */}
                            <Box sx={{
                                display: 'flex', gap: 3, mb: 2.5, flexWrap: 'wrap',
                                alignItems: 'center',
                            }}>
                                {/* Bubble preview */}
                                <Box sx={{
                                    position: 'relative', width: 180, height: 120,
                                    bgcolor: '#f8f9fb', borderRadius: 3,
                                    border: '1px dashed', borderColor: 'divider',
                                    display: 'flex', alignItems: 'flex-end',
                                    justifyContent: 'flex-end', p: 1.5,
                                    overflow: 'hidden',
                                }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            position: 'absolute', top: 6, left: 8,
                                            color: 'text.disabled', fontSize: 10,
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        {isRTL ? 'صفحة موقعك' : 'Your website'}
                                    </Typography>
                                    {/* Mini bubble */}
                                    <Box sx={{
                                        width: 44, height: 44, borderRadius: '50%',
                                        bgcolor: primaryColor,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: `0 3px 12px ${primaryColor}50`,
                                        animation: 'pulse 2.5s ease-in-out infinite',
                                        cursor: 'default',
                                        '@keyframes pulse': {
                                            '0%, 100%': { transform: 'scale(1)' },
                                            '50%': { transform: 'scale(1.08)' },
                                        },
                                    }}>
                                        <ChatIcon sx={{ fontSize: 20, color: '#fff' }} />
                                    </Box>
                                </Box>

                                {/* Options table */}
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                                        {isRTL ? 'الخيارات المتاحة' : 'Available Options'}
                                    </Typography>
                                    {[
                                        { attr: 'data-form-id', desc: isRTL ? 'معرف النموذج (مطلوب)' : 'Form ID (required)', val: formId },
                                        { attr: 'data-color', desc: isRTL ? 'لون الأيقونة' : 'Widget color', val: primaryColor },
                                        { attr: 'data-position', desc: isRTL ? 'الموضع: right / left' : 'Position: right / left', val: 'right' },
                                        { attr: 'data-lang', desc: isRTL ? 'اللغة: en / ar' : 'Language: en / ar', val: 'en' },
                                    ].map((opt) => (
                                        <Box key={opt.attr} sx={{
                                            display: 'flex', alignItems: 'center', gap: 1, py: 0.4,
                                        }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontFamily: 'monospace', bgcolor: 'grey.100',
                                                    px: 0.8, py: 0.2, borderRadius: 1, fontSize: 11,
                                                    color: 'primary.main', fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {opt.attr}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                                                {opt.desc}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>

                            {/* How to use */}
                            <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                    {isRTL ? 'كيفية الاستخدام' : 'How to use'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" component="div">
                                    {isRTL ? (
                                        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                                            <li>انسخ الكود أدناه</li>
                                            <li>الصقه قبل علامة {'</body>'} في صفحة HTML الخاصة بك</li>
                                            <li>ستظهر أيقونة محادثة عائمة في الزاوية اليمنى السفلية</li>
                                            <li>عند النقر عليها، تفتح نافذة محادثة مباشرة</li>
                                        </Box>
                                    ) : (
                                        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                                            <li>Copy the code below</li>
                                            <li>Paste it before the {'</body>'} tag on your HTML page</li>
                                            <li>A floating chat bubble will appear at the bottom-right corner</li>
                                            <li>Clicking it opens a live chat modal window</li>
                                        </Box>
                                    )}
                                </Typography>
                            </Alert>

                            {/* Widget embed codes - EN and AR */}
                            {[
                                { lang: 'en', label: isRTL ? 'كود الموقع الإنجليزي' : 'English Site Code', flag: '🇬🇧' },
                                { lang: 'ar', label: isRTL ? 'كود الموقع العربي' : 'Arabic Site Code', flag: '🇸🇦' },
                            ].map(({ lang, label, flag }) => {
                                const code = `<!-- Live Chat Widget (${lang.toUpperCase()}) -->\n<script>\n  (function() {\n    var d = document, s = d.createElement('script');\n    s.src = '${baseUrl}/chat-widget.js';\n    s.setAttribute('data-form-id', '${formId}');\n    s.setAttribute('data-color', '${primaryColor}');\n    s.setAttribute('data-lang', '${lang}');\n    s.async = true;\n    d.head.appendChild(s);\n  })();\n</script>`
                                return (
                                    <Paper
                                        key={lang}
                                        sx={{
                                            p: 2, bgcolor: '#1a1a2e', borderRadius: 2,
                                            position: 'relative', overflow: 'hidden', mb: 1.5,
                                            '&::before': {
                                                content: `"${flag} ${lang.toUpperCase()}"`, position: 'absolute',
                                                top: 8, left: 12, fontSize: 10,
                                                color: '#1a3a6b', fontFamily: 'monospace',
                                                fontWeight: 700, letterSpacing: 1,
                                            }
                                        }}
                                    >
                                        <Tooltip title={isRTL ? 'نسخ الكود' : 'Copy code'}>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(code)
                                                    toast.success(isRTL ? `تم نسخ كود ${lang.toUpperCase()}` : `${lang.toUpperCase()} embed code copied!`)
                                                }}
                                                sx={{
                                                    position: 'absolute', top: 4, right: 4,
                                                    color: '#8892b0',
                                                    '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
                                                }}
                                            >
                                                <ContentCopy fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Typography
                                            component="pre"
                                            sx={{
                                                fontFamily: '"Fira Code", "Cascadia Code", Consolas, monospace',
                                                fontSize: 11.5, lineHeight: 1.6,
                                                color: '#e2e8f0', whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-all', m: 0, pt: 2, pr: 4,
                                            }}
                                        >
                                            {code}
                                        </Typography>
                                    </Paper>
                                )
                            })}
                        </Box>
                    </Paper>

                    {/* ── 2. Direct Chat Link ── */}
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <ChatIcon fontSize="small" color="primary" />
                            <Typography variant="body2" fontWeight={700}>
                                {isRTL ? 'رابط المحادثة المباشر' : 'Direct Chat Link'}
                            </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                            {isRTL
                                ? 'شارك هذا الرابط مباشرة مع المستخدمين لفتح صفحة محادثة كاملة'
                                : 'Share this link directly with users to open a full-page chat'}
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: 'monospace', wordBreak: 'break-all',
                                        flex: 1, fontSize: 13, color: 'primary.main',
                                    }}
                                >
                                    {publicChatUrl}
                                </Typography>
                                <Tooltip title={isRTL ? 'نسخ' : 'Copy'}>
                                    <IconButton size="small" onClick={handleCopyUrl}
                                        sx={{ bgcolor: 'primary.50', '&:hover': { bgcolor: 'primary.100' } }}
                                    >
                                        <ContentCopy fontSize="small" color="primary" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Paper>
                    </Paper>
                </Box>
            )}
        </Box>
    )
}

export default LiveChatSettings
